import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import { Label } from '@/shared/components/ui/label';
import { Loader2 } from 'lucide-react';
import { copyTaskSchema, type CopyTaskInput } from '../../schemas';
import { useCopyTask } from '../../hooks/useTaskCopyMutations';

const COPY_OPTIONS: Array<{ name: keyof CopyTaskInput; labelKey: string; testId: string }> = [
  { name: 'includeTags', labelKey: 'copy.includeTags', testId: 'tasks-copy-dialog-include-tags' },
  {
    name: 'includeFieldValues',
    labelKey: 'copy.includeFieldValues',
    testId: 'tasks-copy-dialog-include-field-values',
  },
  {
    name: 'includeChecklists',
    labelKey: 'copy.includeChecklists',
    testId: 'tasks-copy-dialog-include-checklists',
  },
  {
    name: 'includeSubtasks',
    labelKey: 'copy.includeSubtasks',
    testId: 'tasks-copy-dialog-include-subtasks',
  },
];

interface CopyTaskDialogProps {
  taskId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CopyTaskDialog({ taskId, open, onOpenChange }: CopyTaskDialogProps) {
  const { t } = useTranslation('tasks');
  const { mutate: copyTask, isPending } = useCopyTask(taskId);

  const form = useForm<CopyTaskInput>({
    resolver: zodResolver(copyTaskSchema),
    defaultValues: {
      includeTags: true,
      includeFieldValues: true,
      includeChecklists: true,
      includeSubtasks: false,
    },
  });

  function handleSubmit(data: CopyTaskInput) {
    copyTask(data, {
      onSuccess: () => {
        form.reset();
        onOpenChange(false);
      },
    });
  }

  function handleOpenChange(value: boolean) {
    if (!value) form.reset();
    onOpenChange(value);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="w-full max-w-md" data-testid="tasks-copy-dialog-root">
        <DialogHeader>
          <DialogTitle>{t('copy.dialogTitle')}</DialogTitle>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4 py-2">
          <div className="space-y-3">
            {COPY_OPTIONS.map(({ name, labelKey, testId }) => (
              <Controller
                key={name}
                control={form.control}
                name={name}
                render={({ field }) => (
                  <div className="flex items-center gap-3">
                    <Checkbox
                      id={testId}
                      checked={!!field.value}
                      onCheckedChange={(checked) => field.onChange(!!checked)}
                      data-testid={testId}
                    />
                    <Label htmlFor={testId} className="cursor-pointer text-sm">
                      {t(labelKey)}
                    </Label>
                  </div>
                )}
              />
            ))}
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={isPending}
            >
              {t('subtasks.confirmDelete.cancel')}
            </Button>
            <Button type="submit" disabled={isPending} data-testid="tasks-copy-dialog-submit-btn">
              {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('copy.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
